import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { getLeaderModuleResults, getAllModuleResults, getSurveyReport, generateSurveySummary, exportSurveyReport } from '../../api/moduleApi';
import { getSystemSettings } from '../../api/adminApi';

const ReviewResults = ({ user, selectedLeader, adminMode }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [reportSurvey, setReportSurvey] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [prevReportData, setPrevReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showTextResponses, setShowTextResponses] = useState(false);
  const [academicYearStartDate, setAcademicYearStartDate] = useState({ month: 9, day: 22 });
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      try {
        // Load academic year settings
        try {
          const settings = await getSystemSettings();
          if (settings.academic_year_start_date) {
            const [m, d] = settings.academic_year_start_date.split('-').map(Number);
            setAcademicYearStartDate({ month: m, day: d });
          }
        } catch (settingsErr) {
          console.warn('Falling back to default academic year settings:', settingsErr);
        }
        
        // Admin mode: fetch all modules; otherwise fetch modules for a specific leader.
        if (adminMode) {
          console.debug('ReviewResults loading in admin mode');
          const res = await getAllModuleResults();
          setData(res || []);
        } else {
          // Accept leader object shape from Header (which uses `account_id`) or older shapes using `id`.
          const leaderId = (selectedLeader && (selectedLeader.account_id || selectedLeader.id)) || (user && user.id);
          console.debug('ReviewResults loading for leader:', { leaderId, selectedLeader, user });
          if (!leaderId) {
            setError('Leader not identified');
            return;
          }
          const res = await getLeaderModuleResults(leaderId);
          setData(res || []);
        }
      } catch (err) {
        console.error('Failed loading module results', err);
        setError(err.message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, selectedLeader, adminMode]);

  const rows = [];
  data.forEach(mod => {
    const leaderName = mod.module_leader_name || mod.leader_name || '';
    if (!mod.surveys || mod.surveys.length === 0) {
      rows.push({ module_code: mod.module_code || mod.module_name, year: '-', responses: '-', avg: '-', module_id: mod.module_id, survey_id: null, leader_name: leaderName, module_name: mod.module_name });
    } else {
      mod.surveys.forEach(s => {
        const responsesCompleted = s.responses_completed || 0;
        const totalEnrolled = s.total_enrolled || 0;
        const responseRate = totalEnrolled ? (responsesCompleted / totalEnrolled) * 100 : 0;
        const pct = s.total_enrolled ? `${Math.round((s.responses_completed / s.total_enrolled) * 100)}% (${s.responses_completed}/${s.total_enrolled})` : `${s.responses_completed} responses`;
        rows.push({
          module_code: mod.module_code || mod.module_name,
          module_name: mod.module_name,
          year: s.survey_year || '-',
          responses: pct,
          avg: s.avg_rating ? s.avg_rating.toFixed(2) : '-',
          avg_numeric: s.avg_rating,
          module_id: mod.module_id,
          survey_id: s.survey_id,
          leader_name: leaderName,
          responses_completed: responsesCompleted,
          total_enrolled: totalEnrolled,
          response_rate: responseRate,
          deployment_date: s.deployment_date,
          status: s.status
        });
      });
    }
  });

  // Extract unique years and sort descending
  const uniqueYears = Array.from(new Set(rows.map(r => r.year).filter(y => y !== '-'))).sort((a, b) => b - a);

  const getCurrentAcademicYearStart = () => {
    const now = new Date();
    const startMonth = Number(academicYearStartDate?.month || 9);
    const startDay = Number(academicYearStartDate?.day || 1);
    const startThisYear = new Date(now.getFullYear(), startMonth - 1, startDay);
    return now >= startThisYear ? now.getFullYear() : now.getFullYear() - 1;
  };
  
  // Set default selected year to current academic year when available
  useEffect(() => {
    if (selectedYear === null && uniqueYears.length > 0) {
      const currentAcademicYear = getCurrentAcademicYearStart();
      if (uniqueYears.includes(currentAcademicYear)) {
        setSelectedYear(currentAcademicYear);
        return;
      }
      const notFutureYears = uniqueYears.filter(y => y <= currentAcademicYear);
      if (notFutureYears.length > 0) {
        setSelectedYear(notFutureYears[0]);
        return;
      }
      setSelectedYear(uniqueYears[0]);
    }
  }, [uniqueYears, selectedYear, academicYearStartDate]);

  // Filter rows by selected year if one is selected
  const filteredRows = selectedYear ? rows.filter(r => r.year === selectedYear || r.year === '-') : rows;

  if (loading) return <div className="text-center py-10 text-indigo-500 font-medium">Loading My Module Data...</div>;
  if (error) return <div className="p-6 bg-red-100 rounded-xl shadow-lg text-red-700 font-medium">Error: {error}</div>;

  const openReport = async (row) => {
    if (!row || !row.survey_id) return;
    setReportSurvey(row);
    setReportData(null);
    setPrevReportData(null);
    setReportError(null);
    setShowTextResponses(false);
    setReportLoading(true);
    try {
      const res = await getSurveyReport(row.survey_id);
      setReportData(res);
      const prev = getPrevSurvey(row.module_id, row.survey_id);
      if (prev && prev.survey_id) {
        try {
          const prevRes = await getSurveyReport(prev.survey_id);
          setPrevReportData(prevRes);
        } catch (prevErr) {
          console.error('Failed to load previous survey report', prevErr);
        }
      }
    } catch (err) {
      setReportError(err.message || 'Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const closeReport = () => {
    setReportSurvey(null);
    setReportData(null);
    setPrevReportData(null);
    setReportError(null);
    setSummaryLoading(false);
    setShowTextResponses(false);
    setIsExportMenuOpen(false);
    setExportLoading(null);
  };

  const handleExport = async (format) => {
    if (!reportSurvey?.survey_id) return;
    setExportLoading(format);
    setIsExportMenuOpen(false);
    try {
      const { blob, filename } = await exportSurveyReport(reportSurvey.survey_id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setReportError(err.message || 'Failed to export report');
    } finally {
      setExportLoading(null);
    }
  };

  const handleGenerateSummary = async () => {
    if (!reportSurvey) return;
    setSummaryLoading(true);
    setReportError(null);
    try {
      const res = await generateSurveySummary(reportSurvey.survey_id, true);
      setReportData(prev => ({
        ...(prev || {}),
        ai_summary: {
          ...(prev && prev.ai_summary ? prev.ai_summary : {}),
          text: res.summary_text,
          generated_by: res.generated_by || 'manual',
          response_count: res.response_count,
          response_pct: res.response_pct,
          status: 'ready'
        }
      }));
    } catch (err) {
      setReportError(err.message || 'Failed to generate summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const getPrevSurvey = (moduleId, surveyId) => {
    const mod = data.find(m => m.module_id === moduleId);
    if (!mod || !mod.surveys || mod.surveys.length === 0) return null;
    const sorted = [...mod.surveys].sort((a, b) => {
      const ya = a.survey_year || 0;
      const yb = b.survey_year || 0;
      if (yb !== ya) return yb - ya;
      const da = a.deployment_date ? new Date(a.deployment_date).getTime() : 0;
      const db = b.deployment_date ? new Date(b.deployment_date).getTime() : 0;
      return db - da;
    });
    const idx = sorted.findIndex(s => s.survey_id === surveyId);
    if (idx === -1 || idx + 1 >= sorted.length) return null;
    return sorted[idx + 1];
  };

  const getPrevQuestionAvg = (questionId) => {
    if (!prevReportData || !prevReportData.question_averages) return null;
    const match = prevReportData.question_averages.find(q => q.question_id === questionId);
    return match && match.avg_score != null ? match.avg_score : null;
  };

  // Trend indicator component
  const TrendIndicator = ({ current, previous }) => {
    if (previous === null || previous === undefined) return <span className="text-gray-400 text-sm">—</span>;
    const delta = current - previous;
    if (Math.abs(delta) < 0.01) return <span className="text-gray-500 text-sm">→</span>;
    if (delta > 0) {
      return (
        <span className="text-green-600 font-bold text-sm inline-flex items-center gap-1">
          ↑ {delta.toFixed(2)}
        </span>
      );
    } else {
      return (
        <span className="text-red-600 font-bold text-sm inline-flex items-center gap-1">
          ↓ {Math.abs(delta).toFixed(2)}
        </span>
      );
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-4 text-indigo-700">My Module Data Review</h2>
      <p className="text-gray-600 mb-6">Review detailed data and summaries for the modules you lead.</p>

      {uniqueYears.length > 0 && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="font-semibold text-gray-700">Filter by Academic Year:</label>
          <select 
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Years</option>
            {uniqueYears.map(year => (
              <option key={year} value={year}>{year}-{year + 1}</option>
            ))}
          </select>
        </div>
      )}

      <div className="mt-4 p-4 border border-gray-200 rounded-lg overflow-x-auto">
        <div className="md:hidden space-y-3">
          {filteredRows.map((r, idx) => (
            <div key={`${r.module_id}-${r.survey_id || 'na'}-${idx}`} className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="font-semibold text-gray-900">{r.module_code}</div>
              {adminMode && <div className="text-sm text-gray-700 mt-1">Leader: {r.leader_name || '-'}</div>}
              <div className="text-sm text-gray-700 mt-1">Academic Year: {r.year === '-' ? '-' : `${r.year}-${r.year + 1}`}</div>
              <div className="text-sm text-gray-700 mt-1">Responses: {r.responses}</div>
              <div className="text-sm text-gray-700 mt-1">Avg. Score: <span className={`${r.avg !== '-' && r.avg >= 4 ? 'text-green-600 font-semibold' : ''}`}>{r.avg}</span></div>
              <div className="mt-2">
                {r.survey_id ? (
                  <button
                    onClick={() => openReport(r)}
                    className="text-blue-600 hover:text-blue-900 font-medium"
                  >
                    Review
                  </button>
                ) : (
                  <span className="text-sm text-gray-500">No survey</span>
                )}
              </div>
            </div>
          ))}
          {filteredRows.length === 0 && <div className="text-sm text-gray-500">No modules or surveys found for your account.</div>}
        </div>

        <table className="hidden md:table min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
              {adminMode && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leader</th>}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Academic Year</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responses</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg. Score</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRows.map((r, idx) => (
              <tr key={`${r.module_id}-${r.survey_id || 'na'}-${idx}`}>
                <td className="px-6 py-4 whitespace-nowrap">{r.module_code}</td>
                {adminMode && <td className="px-6 py-4 whitespace-nowrap">{r.leader_name || '-'}</td>}
                <td className="px-6 py-4 whitespace-nowrap">{r.year === '-' ? '-' : `${r.year}-${r.year + 1}`}</td>
                <td className="px-6 py-4 whitespace-nowrap">{r.responses}</td>
                <td className={`px-6 py-4 whitespace-nowrap ${r.avg !== '-' && r.avg >= 4 ? 'text-green-600 font-semibold' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span>{r.avg}</span>
                    {(() => {
                      if (!r.survey_id || r.avg_numeric == null) return null;
                      const prev = getPrevSurvey(r.module_id, r.survey_id);
                      const prevAvg = prev && prev.avg_rating != null ? prev.avg_rating : null;
                      return prevAvg == null ? null : (
                        <TrendIndicator current={r.avg_numeric} previous={prevAvg} />
                      );
                    })()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {r.survey_id ? (
                    <button
                      onClick={() => openReport(r)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      Review
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">No survey</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRows.length === 0 && <div className="hidden md:block mt-4 text-sm text-gray-500">No modules or surveys found for your account.</div>}
      </div>

      {reportSurvey && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-indigo-700">
                    {reportSurvey.module_code} - {reportSurvey.year === '-' ? '-' : `${reportSurvey.year}-${reportSurvey.year + 1}`}
                  </h3>
                  <p className="text-sm text-gray-500">{reportSurvey.module_name || 'Module'} survey report</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <button
                      onClick={() => setIsExportMenuOpen(prev => !prev)}
                      disabled={!!exportLoading}
                      className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:bg-indigo-400"
                    >
                      {exportLoading ? `Exporting ${exportLoading.toUpperCase()}...` : 'Export'}
                    </button>
                    {isExportMenuOpen && (
                      <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                        <button
                          onClick={() => handleExport('pdf')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Export as PDF
                        </button>
                        <button
                          onClick={() => handleExport('xlsx')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Export as Excel (.xlsx)
                        </button>
                        <button
                          onClick={() => handleExport('csv')}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Export as CSV
                        </button>
                      </div>
                    )}
                  </div>
                  <button onClick={closeReport} className="text-gray-400 hover:text-gray-700">Close</button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {reportLoading && <div className="text-indigo-500">Loading report...</div>}
              {reportError && <div className="p-3 bg-red-100 text-red-700 rounded">{reportError}</div>}

              {!reportLoading && reportSurvey && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-gray-50">
                    <div className="text-xs uppercase text-gray-500">Response Rate</div>
                    <div className="text-2xl font-semibold text-gray-900">
                      {reportSurvey.total_enrolled ? `${Math.round(reportSurvey.response_rate)}%` : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">{reportSurvey.responses_completed}/{reportSurvey.total_enrolled} completed</div>
                  </div>
                  <div className="p-4 rounded-lg bg-gray-50">
                    <div className="text-xs uppercase text-gray-500">Average Rating</div>
                    <div className="text-2xl font-semibold text-gray-900">
                      {reportSurvey.avg !== '-' ? reportSurvey.avg : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Across rating questions</div>
                  </div>
                  <div className="p-4 rounded-lg bg-gray-50">
                    <div className="text-xs uppercase text-gray-500">Year-on-Year</div>
                    {(() => {
                      const prev = getPrevSurvey(reportSurvey.module_id, reportSurvey.survey_id);
                      if (!prev) return <div className="text-sm text-gray-500">No prior survey</div>;
                      const prevAvg = prev.avg_rating;
                      const prevTotal = prev.total_enrolled || 0;
                      const prevCompleted = prev.responses_completed || 0;
                      const prevRate = prevTotal ? (prevCompleted / prevTotal) * 100 : 0;
                      const avgDelta = (reportSurvey.avg_numeric != null && prevAvg != null) ? (reportSurvey.avg_numeric - prevAvg) : null;
                      const rateDelta = reportSurvey.total_enrolled ? (reportSurvey.response_rate - prevRate) : null;
                      return (
                        <div className="text-sm text-gray-700">
                          <div>Prev year: {prev.survey_year ? `${prev.survey_year}-${prev.survey_year + 1}` : 'N/A'}</div>
                          <div>Avg delta: {avgDelta == null ? 'N/A' : `${avgDelta >= 0 ? '+' : ''}${avgDelta.toFixed(2)}`}</div>
                          <div>Response delta: {rateDelta == null ? 'N/A' : `${rateDelta >= 0 ? '+' : ''}${rateDelta.toFixed(1)}%`}</div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {!reportLoading && reportData && (
                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-gray-900">Questions & Scores</h4>
                  {reportData.question_averages && reportData.question_averages.length > 0 ? (
                    <div className="space-y-3">
                      {reportData.question_averages.map((q, idx) => {
                        const prevAvg = getPrevQuestionAvg(q.question_id);
                        const delta = (q.avg_score != null && prevAvg != null) ? q.avg_score - prevAvg : null;
                        const maxDelta = 1;
                        const magnitude = delta != null ? Math.min(Math.abs(delta) / maxDelta, 1) : 0;
                        const barWidth = Math.round(magnitude * 40);
                        return (
                        <div key={`${q.question_id}-${idx}`} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-grow">
                              <p className="text-sm font-medium text-gray-900">{q.question_text}</p>
                              <p className="text-xs text-gray-500 mt-1">{q.response_count} responses</p>
                              {prevReportData ? (
                                <div className="mt-2 text-xs text-gray-600">
                                  Prev avg: {prevAvg == null ? 'N/A' : prevAvg.toFixed(1)}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-3 whitespace-nowrap">
                              <div className="text-right">
                                <p className="text-2xl font-bold text-indigo-600">{q.avg_score ? q.avg_score.toFixed(1) : 'N/A'}</p>
                                <p className="text-xs text-gray-500">/5</p>
                              </div>
                              <div className="min-w-[40px] text-right">
                                <TrendIndicator current={q.avg_score} previous={prevAvg} />
                                {delta != null ? (
                                  <div className="mt-1 h-1 w-10 bg-gray-200 rounded">
                                    <div
                                      className={`h-1 rounded ${delta >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                                      style={{ width: `${barWidth}px` }}
                                    />
                                  </div>
                                ) : (
                                  <div className="mt-1 h-1 w-10 bg-gray-100 rounded" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No rating questions in this survey.</p>
                  )}
                </div>
              )}

              {!reportLoading && reportData && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">AI Free-Text Summary</h4>
                    <button
                      onClick={handleGenerateSummary}
                      disabled={summaryLoading}
                      className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 disabled:bg-indigo-400"
                    >
                      {summaryLoading ? 'Generating...' : 'Generate Summary'}
                    </button>
                  </div>

                  {reportData.ai_summary?.status === 'ready' && reportData.ai_summary?.text && (
                    <div className="p-4 rounded-lg bg-gray-50 prose prose-sm max-w-none prose-headings:text-indigo-700 prose-strong:text-indigo-900 prose-strong:font-semibold prose-ul:list-disc prose-ol:list-decimal">
                      <ReactMarkdown
                        components={{
                          h1: ({node, ...props}) => <h2 className="text-xl font-bold text-indigo-700 mt-4 mb-2" {...props} />,
                          h2: ({node, ...props}) => <h3 className="text-lg font-bold text-indigo-700 mt-3 mb-2" {...props} />,
                          h3: ({node, ...props}) => <h4 className="font-bold text-indigo-600 mt-2 mb-1" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-indigo-900" {...props} />,
                          em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside my-2 ml-2" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside my-2 ml-2" {...props} />,
                          li: ({node, ...props}) => <li className="my-1" {...props} />,
                          p: ({node, ...props}) => <p className="my-2 leading-relaxed" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-indigo-300 pl-3 py-1 my-2 italic text-gray-700" {...props} />,
                        }}
                      >
                        {reportData.ai_summary.text}
                      </ReactMarkdown>
                    </div>
                  )}

                  {reportData.ai_summary?.status !== 'ready' && (
                    <div className="text-sm text-gray-600">
                      Auto summary will generate when responses reach {reportData.auto_generate_settings?.min_response_pct ?? 100}%
                      or after {reportData.auto_generate_settings?.delay_days ?? 30} days.
                    </div>
                  )}

                  {reportData.ai_summary?.status === 'no_text_responses' && (
                    <div className="text-sm text-gray-500">No text responses available yet.</div>
                  )}
                </div>
              )}

              {!reportLoading && reportData && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">Free-Text Responses</h4>
                    <button
                      onClick={() => setShowTextResponses(prev => !prev)}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {showTextResponses ? 'Hide Responses' : 'Show Responses'}
                    </button>
                  </div>

                  {showTextResponses && (
                    <div className="space-y-4">
                      {(reportData.text_responses || []).length === 0 && (
                        <div className="text-sm text-gray-500">No text responses available yet.</div>
                      )}
                      {(reportData.text_responses || []).map((group, idx) => (
                        <div key={`${group.question_text}-${idx}`} className="p-4 rounded-lg bg-gray-50">
                          <div className="font-semibold text-gray-800 mb-2">{group.question_text}</div>
                          {(group.responses || []).length === 0 ? (
                            <div className="text-sm text-gray-500">No responses yet.</div>
                          ) : (
                            <ul className="space-y-2 text-sm text-gray-700">
                              {group.responses.map((resp, rIdx) => (
                                <li key={`${idx}-${rIdx}`} className="border-l-2 border-indigo-200 pl-2">{resp}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewResults;